import {useFieldArray, useForm, useWatch} from "react-hook-form";
import React, {useEffect, useMemo} from "react";
import {ModalPanelForm} from "@components/Modal2023";
import {InputCleave, InputEx} from "@components/wrapperComponents/InputEx";
import FormButtons, {TraceManager} from "@components/FormButtons";
import {PresenzeGiornataPlusRecord} from "@commons/mapping/PresenzeGiornataPlusRecord";
import BannerDay from "./BannerDay";
import {LavoratorePresenzeRecord} from "@commons/mapping/LavoratorePresenzeRecord";
import {CoppiaTimbrature} from "@commons/mapping/CoppiaTimbrature";
import {ComboBoxTurno} from "@components/comboBox/ComboBoxTurni";
import {TurnoRecord} from "@commons/mapping/TurnoRecord";
import Toggle from "@components/Toggle";
import {TurnoDistribuzioneRecord} from "@commons/mapping/TurnoDistribuzioneRecord";
import {FieldValues} from "react-hook-form/dist/types";
import {Coppia} from "@commons/mapping/Calc";
import {RiepilogoOrePlus} from "@commons/mapping/RiepilogoOrePlus";
import {GiustificativoRecord} from "@commons/mapping/GiustificativoRecord";
import {FormUtils} from "@utilities/FormUtils";

export type EditPrevistoFormValues = {
    codTurno: number,
    coppie: CoppiaTimbrature[],
    previsteLocked: boolean,
    hhPreviste: string
}

interface EditPrevistoProps {
    lavoratore: LavoratorePresenzeRecord,
    day: PresenzeGiornataPlusRecord,
    turni: TurnoRecord[],
    giustificativi: GiustificativoRecord[],
    onSave: (day: PresenzeGiornataPlusRecord, values: EditPrevistoFormValues, dirtyValues: Partial<EditPrevistoFormValues>) => void,
    onCancelEdit: () => void,
    onResetOrario: (day: PresenzeGiornataPlusRecord) => void,
}

// Modal per l'editing del previsto
export const EditPrevisto: React.FC<EditPrevistoProps> = ({
                                                              lavoratore,
                                                              day,
                                                              turni,
                                                              giustificativi,
                                                              onSave,
                                                              onCancelEdit,
                                                              onResetOrario
                                                          }) => {
    const numMaxCoppie = 5;

    const traceManager = useMemo<TraceManager>(() => {
        return {
            getDeleteInfo: (_trace: PresenzeGiornataPlusRecord) => {
                return undefined;
            }
        }
    }, []);

    // Metodo per inizializzare i valori del form sulla base della giornata indicata in <day>
    const setupDefaultValues = (day: PresenzeGiornataPlusRecord): EditPrevistoFormValues => {
        const values: CoppiaTimbrature[] = [];
        if (day && day.previste && day.previste.length) {
            day.previste.forEach(coppia => {
                if (!CoppiaTimbrature.isNull(coppia)) {
                    const newCoppia = CoppiaTimbrature.factory(coppia);
                    values.push(newCoppia);
                }
            });
        }
        while (values.length < numMaxCoppie) {
            const newCoppia = CoppiaTimbrature.factory();
            values.push(newCoppia);
        }
        return {coppie: values, previsteLocked: day.previsteLocked, codTurno: day.codTurno, hhPreviste: RiepilogoOrePlus.getTotaleHPrev(day.totali).toString()};
    };

    const defaultValues = useMemo(() => setupDefaultValues(day), [day]);
    const {
        register, watch, getValues, setValue, control, handleSubmit,
        formState: {errors, isDirty, dirtyFields},
    } = useForm<EditPrevistoFormValues>({
        defaultValues,
        mode: 'onChange',
        reValidateMode: 'onChange'
    });
    const previsteLocked = watch("previsteLocked");
    // useFieldArray permette di gestire form dinamici sulla base di un array di oggetti
    // vedi: https://www.youtube.com/watch?v=4MrbfGSFY2A
    const {fields,} = useFieldArray(
        {
            name: 'coppie',
            control,
            rules: {validate: () => true}
        });

    // Metodo per "pulire" le coppie di timbrature previste
    const clearPreviste = () => {
        for (let ind0 = 0; ind0 < numMaxCoppie; ++ind0) {
            setValue(`coppie.${ind0}.entrata`, '');
            setValue(`coppie.${ind0}.uscita`, '');
        }
    }

    // Metodo per impostare le coppie di timbrature previste sulla base del turno indicato
    const impostaPrevisto = (turno: TurnoRecord | null) => {
        if (turno) {
            setValue('previsteLocked', true, {shouldDirty: true})
            clearPreviste();
            const distribuzione: TurnoDistribuzioneRecord = TurnoRecord.getDistribuzione(turno, day.idGiornoSviluppo, undefined);
            if (distribuzione.hhPreviste > 0) {
                setValue('coppie.0.entrata', distribuzione.prima?.entrata || null);
                setValue('coppie.0.uscita', distribuzione.prima?.uscita || null);
                setValue('coppie.1.entrata', distribuzione.seconda?.entrata || null);
                setValue('coppie.1.uscita', distribuzione.seconda?.uscita || null);
                setValue('coppie.2.entrata', distribuzione.terza?.entrata || null);
                setValue('coppie.2.uscita', distribuzione.terza?.uscita || null);
                setValue('coppie.3.entrata', distribuzione.quarta?.entrata || null);
                setValue('coppie.3.uscita', distribuzione.quarta?.uscita || null);
            }
        }
    }

    const onToggle = (v: boolean) => {
        setValue('previsteLocked', v, {shouldDirty: true})
        if (v) {
            const codTurno = getValues('codTurno');
            const turno = turni.find(t => t.codTurno === codTurno);
            if (turno)
                impostaPrevisto(turno);
        }
    };

    // watch per array coppie
    // la versione watch('coppie') NON va usata per gli array!!!
    const coppie = useWatch({
        name: 'coppie',
        control
    });
    useEffect(() => {
        if (!errors.coppie) {
            let hhPreviste = 0;
            coppie.forEach(coppiaEntrataUscita => {
                const coppia = new Coppia(coppiaEntrataUscita);
                hhPreviste += coppia.calc.getCount();
            });
            setValue('hhPreviste', (hhPreviste / 60).toFixed(2));
        } else
            setValue('hhPreviste', '');
    }, [errors.coppie, coppie, setValue]);

    const onChangeOraInizio = (_v: any) => {
    };
    const onChangeOraFine = (_v: any) => {
    };

    const options = {
        time: true, timePattern: ['h', 'm'], blocks: [2, 2],
        delimiter: ':', timeFormat: '24'
    };

    const formatOrario = (v: string | null, index: number) => {
        if (coppie[index].entrata || coppie[index].uscita)
            return (v && (v.length === 4));
        else
            return true;
    }

    const previsteToggleLock = <Toggle initialState={day.previsteLocked}
                                       auxState={previsteLocked}
                                       onChange={onToggle}
                                       label={previsteLocked ? 'Previsto da turno' : 'Previsto manuale'}
                                       labelPosition="right"/>;
    const compareElementi = (a: TurnoRecord, b: TurnoRecord) => {
        return TurnoRecord.compare(a, b);
    }

    /*
        const watchFields = watch();
        console.log(isDirty, dirtyFields, watchFields);

        console.log(errors.hhPreviste);
    */
    return <ModalPanelForm isOpen={!!day} formControl={control} handleDismiss={onCancelEdit}
                           header={<BannerDay modal={'orario'} day={day} lavoratore={lavoratore} giustificativi={giustificativi} turni={turni} onResetOrario={onResetOrario}
                                              anomalia={day.anomalia}
                                              name="Pannello Orario Previsto" page='Pianificazione'/>}>

        <form className="p-2 bg-white shadow-md rounded xl:min-w-[1130px]"
              onSubmit={handleSubmit(v => {
                  console.log(isDirty, dirtyFields);
                  onSave(day, v, FormUtils.getDirtyFields(dirtyFields, v) as Partial<EditPrevistoFormValues>);
              })
              }>
            <div className="min-h-[480px] flex flex-col">
                <div className="mb-auto">
                    <div className="mb-3">
                        <div className="flex flex-row items-start space-x-2 2xl:space-x-4">
                            <div className="basis-2/6">
                                <ComboBoxTurno<EditPrevistoFormValues> label="Turno"
                                                                       control={control} name={`codTurno`}
                                                                       defaultValue={null}
                                                                       data={turni
                                                                           .filter((item) => item.codTurno === day.codTurno // turno corrente
                                                                               || (item.ggRicorsivita === 1 && item.usaInPianificazione)).sort(compareElementi) // turni alternativi
                                                                       }
                                                                       isLoading={false}
                                                                       valueAsObject={false}
                                                                       onChange={impostaPrevisto}
                                                                       disabled={false}
                                                                       rules={{required: true}}
                                />
                            </div>
                            <div className="basis-2/6">
                                {previsteToggleLock}
                            </div>
                            <div className="basis-2/6">
                                <InputEx<EditPrevistoFormValues>
                                    label={`Totale ore "previste" (in centesimi)`}
                                    name="hhPreviste"
                                    register={register}
                                    registerOptions={{
                                        validate: {
                                            nonZero: v => previsteLocked // se mantengo quanto indicato dal turno
                                                || Number.parseFloat(defaultValues.hhPreviste) === 0 // se erano già in precedenza zero
                                                || Number.parseFloat(v) > 0 // se sono maggiori di zero
                                                || 'Le ore devono essere maggiori di zero!'
                                        }
                                    }}
                                    readOnly
                                    errors={errors}
                                />
                            </div>
                        </div>
                    </div>

                    {fields.map((field, index) => {
                        return <section key={field.id}>
                            <div className="mb-3">
                                <div className="flex flex-row items-start space-x-2 2xl:space-x-4">
                                    <div className="basis-1/6">
                                        <InputCleave<EditPrevistoFormValues>
                                            options={options}
                                            rules={{
                                                validate: {
                                                    format: v => formatOrario(v as string | null, index) || 'Utilizzare il formato hh:mm',
                                                }
                                            }}
                                            label={`${index + 1}° entrata`}
                                            name={`coppie.${index}.entrata`}
                                            placeholder="hh:mm"
                                            disabled={previsteLocked}
                                            control={control}
                                            onChange={(v: string) => onChangeOraInizio(v)}
                                        /></div>
                                    <div className="basis-1/6">
                                        <InputCleave<EditPrevistoFormValues>
                                            options={options}
                                            rules={{
                                                validate: {
                                                    format: v => formatOrario(v as string | null, index) || 'Utilizzare il formato hh:mm',
                                                }
                                            }}
                                            label={`${index + 1}° uscita`}
                                            name={`coppie.${index}.uscita`}
                                            placeholder="hh:mm"
                                            disabled={previsteLocked}
                                            control={control}
                                            onChange={(v: string) => onChangeOraFine(v)}
                                        />
                                    </div>
                                    <div className="basis-4/6">
                                    </div>
                                </div>

                                <div className="flex flex-row items-start space-x-2 2xl:space-x-4">
                                    <div className="basis-3/6"/>
                                    <div id={`errorQuantita${index}`}></div>
                                </div>
                            </div>
                        </section>
                    })}
                </div>
                <div className="">
                    <FormButtons onCancel={onCancelEdit} resource={day} traceManager={traceManager}/>
                </div>
            </div>
        </form>

    </ModalPanelForm>
}
export default EditPrevisto;

// https://github.com/orgs/react-hook-form/discussions/1991
// Da approfondire!!!
const filterChangedFormFields = <T extends FieldValues>(
    allFields: T,
    dirtyFields: Partial<Record<keyof T, boolean | boolean[]>>
): Partial<T> => {
    const changedFieldValues = Object.keys(dirtyFields).reduce(
        (acc, currentField) => {
            const isDirty = Array.isArray(dirtyFields[currentField])
                ? (dirtyFields[currentField] as boolean[]).some((value) => value === true)
                : dirtyFields[currentField] === true;
            if (isDirty) {
                return {
                    ...acc,
                    [currentField]: allFields[currentField],
                };
            }
            return acc;
        },
        {} as Partial<T>
    );

    return changedFieldValues;
};
